import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EditorTestCases from "./editor-testcases/editorTestCases";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import { cn } from "@lxp-app/utils/cn";
import LoaderIcon from "@assessment/components/atoms/loaderIcon";
import Button from "@lxp-app/components/atoms/button";
import { useAppSelector } from "@assessment/store/store";
import Modal from "@lxp-app/components/atoms/modal";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";

interface ActionProp {
  editorTheme: "vs-dark" | "light";
  runTestCases: () => void;
  submitAnswers: () => void;
  setShowLoader: (arg: boolean) => void;
  showLoader: boolean;
  testCaseType: "sample" | "hidden";
  preferredTestCaseHeight: number;
  height: number;
  setHeight: (arg: number) => void;
  testCaseResult: any;
  runTimeError: string;
  autoSave: boolean;
  showTestCases: boolean;
  setShowTestCases: (arg: any) => void;
  resetToStarterCode: () => void;
}

const EditorActionComponent = ({
  editorTheme,
  runTestCases,
  submitAnswers,
  showLoader,
  testCaseType,
  preferredTestCaseHeight,
  height,
  setHeight,
  testCaseResult,
  setShowLoader,
  runTimeError,
  autoSave,
  showTestCases,
  setShowTestCases,
  resetToStarterCode
}: ActionProp) => {
  const { t } = useTranslation();
  const [statusType, setStatusType] = useState("save");
  const [modalOpen, setModalOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const isOnline = useAppSelector((store) => store.AssessmentSlice?.isOnline);
  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  useEffect(() => {
    if (isOnline) {
      setStatusType("online");
      setTimeout(() => {
        setStatusType("save");
      }, 2000);
    } else {
      setStatusType("offline");
    }
  }, [isOnline]);

  useEffect(() => {
    setShowTestCases(false);
  }, [activeQuestionMeta?.questionId]);

  const toggleTestCases = () => {
    setShowTestCases((prev: any) => {
      const shouldShow = !prev;
      setHeight(shouldShow ? preferredTestCaseHeight : 0);
      return shouldShow;
    });
  };

  const handleModal = (): void => {
    setModalOpen(!modalOpen);
  };

  return (
    <>
      <div>
        <div className="relative w-full mt-4">
          {/* Horizontal line */}
          <div className="border-t border-gray-300 w-full absolute top-1/2 transform -translate-y-1/2" />

          {/* Circle with icon */}
          <div className="flex justify-center">
            <div
              className={`relative z-10 border border-gray-300 rounded-full w-8 h-8 flex items-center justify-center
            ${editorTheme === "vs-dark" && "bg-gray-800 text-white"}
              ${editorTheme === "light" && "bg-white text-gray-500"}`}
              onClick={() => toggleTestCases()}
            >
              {showTestCases ? (
                <GetHeroIcon
                  name="ChevronDownIcon"
                  variant="outline"
                  className="flex-shrink-0 w-4 h-4 "
                />
              ) : (
                <GetHeroIcon
                  name="ChevronUpIcon"
                  variant="outline"
                  className="flex-shrink-0 w-4 h-4"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between w-full gap-5 px-5 mb-4">
          <p
            className={cn(
              "flex items-center gap-1 text-xs font-medium",
              editorTheme === "vs-dark" && "text-white",
              editorTheme === "light" && "text-gray-500"
            )}
          >
            {autoSave ? (
              <>
                <LoaderIcon />
                <span>{t("assessment.coding.saving")}</span>
              </>
            ) : (
              <div className="flex gap-2">
                <GetHeroIcon
                  name={
                    statusType == "offline"
                      ? "ExclamationTriangleIcon"
                      : statusType !== "save"
                        ? "CheckCircleIcon"
                        : "InformationCircleIcon"
                  }
                  variant="outline"
                  className={cn(
                    "w-5 h-5",
                    statusType == "offline" &&
                      editorTheme === "vs-dark" &&
                      "w-8 h-8 text-yellow-500",
                    statusType == "offline" &&
                      editorTheme === "light" &&
                      "w-8 h-8 text-[#ffb00a]",
                    statusType == "online" &&
                      editorTheme === "vs-dark" &&
                      "w-8 h-8 text-[#28c244]",
                    statusType == "online" &&
                      editorTheme === "light" &&
                      "w-8 h-8 text-[#01b328]"
                  )}
                />
                {statusType == "offline" && (
                  <div className="text-secondary">
                    <div
                      className={cn(
                        "font-semibold",
                        editorTheme === "vs-dark" && "text-gray-300",
                        editorTheme === "light" && "text-gray-500"
                      )}
                    >
                      {t("assessment.coding.offlineTitle")}
                    </div>
                    <div
                      className={`${editorTheme === "vs-dark"} ? text-gray-500 : text-gray-400"`}
                    >
                      {t("assessment.coding.offlineDescription")}
                    </div>
                  </div>
                )}
                {statusType == "online" && (
                  <>
                    <div
                      className={cn(
                        editorTheme === "vs-dark" && "text-[#28c244]",
                        editorTheme === "light" && "text-[#01b328]"
                      )}
                    >
                      {t("assessment.coding.connectionRestored")}
                    </div>
                    <div
                      className={`${editorTheme === "vs-dark"} ? text-gray-500 : text-gray-400"`}
                    >
                      {t("assessment.coding.syncingLatest")}
                    </div>
                  </>
                )}

                {statusType == "save" && (
                  <div className="leading-[1.2] text-gray-400 ml-2">
                    <p className="m-0">
                      {showLoader
                        ? t("assessment.coding.saving")
                        : t("assessment.coding.autoSaveEnabled")}{" "}
                      <span
                        className={`${editorTheme === "light" ? "font-bold" : "text-white font-semibold"}`}
                      >
                        {t("assessment.coding.run")}
                      </span>{" "}
                      {t("assessment.coding.runOnlyChecksDescription")}
                    </p>
                    <p className="m-0">
                      <span
                        className={`${editorTheme === "light" ? "font-bold" : "text-white font-semibold"}`}
                      >
                        {t("assessment.coding.submit")}
                      </span>{" "}
                      {t("assessment.coding.submitEvaluationDescription")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </p>
          <div className="flex items-center gap-5">
            <Button
              buttonColor="secondary"
              variant="outlined"
              disable={showLoader}
              onClick={() => {
                if (!showLoader) handleModal();
              }}
              className={cn(
                "px-3 py-2 bg-transparent rounded-md",
                editorTheme === "vs-dark" &&
                  "text-neutral-300 border border-gray-700",
                showLoader && "cursor-not-allowed"
              )}
            >
              {t("assessment.coding.reset")}
            </Button>

            {!showLoader || testCaseType == "hidden" ? (
              <Button
                buttonColor="secondary"
                variant="contained"
                disable={showLoader}
                onClick={() => runTestCases()}
                className={cn(
                  "px-3 py-2 bg-slate-600 rounded-md",
                  editorTheme === "vs-dark" &&
                    "text-neutral-300 border border-gray-700",
                  showLoader && "cursor-not-allowed"
                )}
              >
                {t("assessment.coding.run")}
              </Button>
            ) : (
              <div
                className={cn(
                  "px-3 py-2 text-white rounded-md bg-slate-600 flex items-center w-full justify-center",
                  editorTheme === "vs-dark" &&
                    "text-neutral-300 border border-gray-700 disabled:bg-gray-100 disabled:text-gray-400",
                  editorTheme === "light" &&
                    "disabled:bg-gray-200 disabled:text-gray-400"
                )}
              >
                <LoaderIcon />
                <p className="text-sm font-semibold">
                  {t("assessment.coding.runTests")}
                </p>
              </div>
            )}

            {!showLoader || testCaseType == "sample" ? (
              <Button
                buttonColor="primary"
                variant="contained"
                disable={showLoader}
                onClick={() => submitAnswers()}
                className={cn(
                  "px-3 py-2 rounded-md",
                  editorTheme === "vs-dark" && "text-neutral-300",
                  showLoader && "cursor-not-allowed"
                )}
              >
                {t("assessment.coding.submit")}
              </Button>
            ) : (
              <div
                className={cn(
                  "px-3 py-2 text-white rounded-md bg-primary flex items-center w-full justify-center",
                  editorTheme === "vs-dark" &&
                    "text-neutral-300 border border-gray-700 disabled:bg-gray-100 disabled:text-gray-400",
                  editorTheme === "light" &&
                    "disabled:bg-gray-200 disabled:text-gray-400"
                )}
              >
                <LoaderIcon />
                <p className="text-sm font-semibold">
                  {t("assessment.coding.submitting")}
                </p>
              </div>
            )}
          </div>
        </div>
        {/* Test Cases */}
        {showTestCases && (
          <div
            id="div3"
            ref={containerRef}
            className={cn(
              "relative flex min-h-0 flex-col overflow-hidden rounded-br-2xl border transition-[height] duration-300 ease-in-out",
              editorTheme === "vs-dark" && "border-gray-500",
              editorTheme === "light" && "border-gray-300"
            )}
            style={{ height: `${height}px`, flexShrink: 0 }}
          >
            {/* Editor Content */}
            <EditorTestCases
              editorTheme={editorTheme}
              testCaseResult={testCaseResult}
              showLoader={showLoader}
              setShowLoader={setShowLoader}
              testcaseType={testCaseType}
              runTimeError={runTimeError}
            />
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        setOpen={handleModal}
        dialogueClassName="sm:max-w-3xl"
      >
        <ContentDeleteModal
          isShow={modalOpen}
          show={() => {
            resetToStarterCode();
            handleModal();
          }}
          setOpen={handleModal}
          buttonName={t("assessment.coding.reset")}
          heading={t("assessment.coding.resetCode")}
          commentsAllowed={false}
          msg={t("assessment.coding.resetCodeMessage")}
        />
      </Modal>
    </>
  );
};

export default EditorActionComponent;
